import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Timeout from 'await-timeout';
import { COPY_STRINGS } from 'Constants/translatedCopy';
import { getUserData } from "Services/API";
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions";

const promptTimer = new Timeout();

const PROMPT_TIMEOUT = 10000;

export default class InterventionMultipleChoice extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      selected: [],
      tries: 0,
      selectionHistory: [],
      correct: false,
      highlighted: null,
    };
    this.startTimer = this.startTimer.bind(this);
  }

  componentDidMount(){
    if(this.props.introText){
      this.props.speech.abortSpeech(() => {
        this.props.speech.speak({
          textToSpeak: this.props.introText,
          onEnd: () => this.speakQuestion(() => {
            this.speakAllOptions(this.startTimer.bind(this));
          }),
        });
      });
    }else {
      //When no introduction, try checking if we're reading questions
      this.speakQuestion(() => {
        this.speakAllOptions(this.startTimer.bind(this));
      });
    }
  }

  componentWillUnmount(){
    promptTimer.clear();
  }

  startTimer(){
    promptTimer.set(PROMPT_TIMEOUT).then(() => {
      if(this.state.selected.length == 0){
        this.props.speech.speak({
          textToSpeak: this.getRandomArrayElement(COPY_STRINGS.intervention_strings.comprehensionPrompt),
        });
      }
    });
  }

  getRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
  }

  getRandomArrayElement(inArray){
    let randomElement = inArray[this.getRandomInt(0, inArray.length)];
    return (randomElement);
  }

  isCorrect(selected){
    //If the arrays are not even the same length then it's not correct
    if(this.props.correctAnswer.length != selected.length){
      return false;
    }
    //If the arrays are the same length then compare every selected answer with the correctAnswer array
    for(let answer of selected){
      if(!this.props.correctAnswer.includes(answer))//If we have a wrong answer return false
        return false;
    }
    //If no wrong answers were found then return true
    return true;
  }

  selectOption(optionSelected){
    let getNewSelections = () => {
      let newSelections = JSON.parse(JSON.stringify(this.state.selected));
      newSelections.push(optionSelected);
      if(newSelections.length > this.props.correctAnswer.length){
        newSelections.shift();
      }
      return newSelections;
    }

    let handleSelection = () => {
      if(!this.state.selected.includes(optionSelected)){
        let newSelections = getNewSelections();
        let correct = this.isCorrect(newSelections);
        this.setState({
          selected: newSelections,
          correct: correct,
          selectionHistory: this.state.selectionHistory.concat("selected " + optionSelected),
        }, () => this.props.updateResults(this.state.tries, this.state.selectionHistory, optionSelected, newSelections, correct));
        return;
      }
    }

    if(this.props.readQuestion){
      this.props.speech.abortSpeech(() => {        
        this.setState({
          highlighted: null,
        }, handleSelection);
      });
    } else {
      handleSelection();
    }
  }

  speakQuestion(onEnd=null){
    if(!this.props.readQuestion){
      onEnd && onEnd();
      return;
    }
    this.props.speech.abortSpeech(() => {
      if(this.props.bilingualQuestion && this.props.bilingualQuestion.speechArray && getUserData().bilingualMode){
        this.props.speech.speakSequence({
          textToSpeak: this.props.bilingualQuestion.speechArray,
          onEnd: onEnd,
        });
      } else {
        let title = this.props.bilingualQuestion ? this.props.bilingualQuestion.english : this.props.questionTitle;
        title = title.replace("____", "<BLANK/>"); //for backward compatibility
        let blank = COPY_STRINGS.blank;
        let speechProps = {
          textToSpeak: '<break time="0.5s"/>' + title.replace("<BLANK/>", "<break time=\"0.2s\" /> " + blank + " <break time=\"0.2s\" />"),
          passThroughSpeechTags: true,
          skipTranslation: false,
          onEnd: onEnd,
        };
        this.props.speech.speak(speechProps);
      }
    });
  }

  speakAllOptions(callback){
    if(!this.props.readQuestion){
      callback && callback();
      return null;
    }
    let speakNextOption = () => {
      let option = this.props.options[this.state.highlighted];
      let isLastOption = this.state.highlighted >= this.props.options.length - 1;
      if(option){
        let textToSpeak = "";
        let lastDelimiter = COPY_STRINGS.or;
        if(isLastOption){
          textToSpeak = '<break time="0.2s"/> ' + lastDelimiter + '<prosody rate="95%">' + option + '</prosody>.';
        } else {
          textToSpeak = '<break time="0.2s"/><prosody rate="95%">' + option + "</prosody>,";
        }

        this.props.speech.speak({
          textToSpeak: textToSpeak,
          passThroughSpeechTags: true,
          skipTranslation: false,
          onEnd: () => {
            if(isLastOption){
              this.setState({highlighted: null}, callback);
            } else {
              this.setState(
                {highlighted: this.state.highlighted + 1},
                speakNextOption
              );
            }
          }
        });
      }
    }

    this.setState({highlighted: 0}, speakNextOption);
  }

  getOptions(){
    let optionsClasses = cx({
      "multiple-options": true,
      "flat": this.props.flat,
      "keepTall": this.props.keepTall,
    });
    if(this.props.flat){
      return(
        <div className={optionsClasses}>
          {this.props.options.map((option, index) => {
            const liClass = this.state.selected.includes(option) ? " selected" : "";
            let highlightedClass = this.state.highlighted == index ? " highlightedOption" : "";
            return (
              <span className={"labelFontOverride option" + liClass + highlightedClass} key={option} {...doNotUseAccessibleStaticElementInteractions({onClick: this.selectOption.bind(this, option)})}>
                {option}
              </span>
            );
          })}
        </div>
      );
    } else {
      return(
        <div className={optionsClasses}>
          <ol className="list">
          {this.props.options.map((option, index) => {
            const liClass = this.state.selected.includes(option) ? " selected" : "";
            let highlightedClass = this.state.highlighted == index ? " highlightedOption" : "";//TODO abstract this more with the above code
            return (
              <li key={option}>
                <span className={"labelFontOverride option" + liClass + highlightedClass} {...doNotUseAccessibleStaticElementInteractions({onClick: this.selectOption.bind(this, option)})}>
                  {option}
                </span>
              </li>
            );
          })}
          </ol>
        </div>
      );
    }
  }

  render(){
    const { questionTitle } = this.props;
    let title = questionTitle;
    title = title.replace("____", "<BLANK/>"); //for backward compatibility
    let splitTitle = title.split("<BLANK/>");
    if(splitTitle.length == 2){
      title = (
        <span>
          {splitTitle[0]}
          <span className={`underline ${false /*this.state.correct*/ ? "correct" : ""}`}>
            {this.state.selected && this.state.selected.length == 1 && this.state.selected[0]}
          </span>
           {splitTitle[1]}
        </span>);
    } else {
      title = title.replace("<BLANK/>", "_______");
    }

    let titleClasses = cx({
      "question-title": true,
      "labelFontOverride": true,
      "clickable": this.props.readQuestion,
    });
    return (
      <div className="multiple-wrapper">
        <div className={titleClasses} {...doNotUseAccessibleStaticElementInteractions({onClick: this.speakQuestion.bind(this, null)})}>
          {title}
        </div>
        {this.getOptions()}
      </div>
    );
  }
}

InterventionMultipleChoice.propTypes = {
  options: PropTypes.array.isRequired,
  correctAnswer: PropTypes.array.isRequired,
  questionTitle: PropTypes.string,
  updateResults: PropTypes.func.isRequired,
};